import { isAutosCategory } from '@kijiji/category'
import { type UserType } from '@kijiji/generated/graphql-types'
import { ChevronIcon } from '@kijiji/icons/src/icons'
import { useTranslation } from 'next-i18next'
import { type FC } from 'react'
import { useTheme } from 'styled-components'

import { ImageCustom } from '@/components/shared/image'
import {
  BannerType,
  DEALER_REQUEST_CTA_ASSETS,
  TRADE_IN_CATEGORIES,
} from '@/components/vip/vip-dealer-request-cta/constants/vipDealerRequestCta'
import {
  VipDealerRequestCtaBanner,
  VipDealerRequestCtaChevron,
  VipDealerRequestCtaWrapper,
} from '@/components/vip/vip-dealer-request-cta/styled'
import { VipFinancingModal } from '@/components/vip/vip-financing/VipFinancingModal'
import { VipTradeInModal } from '@/components/vip/vip-trade-in/VipTradeInModal'
import { TRANSLATION_KEYS } from '@/constants/localization'
import { MODAL_REGISTRY } from '@/features/modal/constants/modalRegistry'
import { useModal } from '@/features/modal/hooks/useModal'
import { isAutosDealerProfile } from '@/features/profile/utils/isAutosDealerProfile'
import { trackEvent } from '@/lib/ga'
import { type VipQueryListing } from '@/types/listings'
import { BodyText } from '@/ui/atoms/body-text'
import { Button } from '@/ui/atoms/button'
import { Flex } from '@/ui/atoms/flex'
import { HeadlineText } from '@/ui/atoms/headline-text'

export type VipDealerRequestCtaProps = {
  bannerType: BannerType
  categoryId: number
  externalSourceId?: string
  flags: VipQueryListing['flags']
  listingId: string
  listingUrl: string
  userType?: UserType
}

export const VipDealerRequestCta: FC<VipDealerRequestCtaProps> = ({
  bannerType,
  categoryId,
  externalSourceId,
  flags,
  listingId,
  listingUrl,
  userType,
}) => {
  const { handleOpenModal: openFinancingModal } = useModal(MODAL_REGISTRY.LISTING_FINANCING)
  const { handleOpenModal: openTradeInModal } = useModal(MODAL_REGISTRY.TRADE_IN)

  const { t } = useTranslation([TRANSLATION_KEYS.LISTING, TRANSLATION_KEYS.TRADE_IN])
  const { spacing, colors } = useTheme()

  /**
   * The financing section should only be shown if the "financingAvailable" flag is true or if it's
   * a dealer listing in the Cars & Trucks/Classic Cars categories.
   */
  const availableOnNpv = 'financingAvailable' in flags ? flags.financingAvailable : false
  const availableOnPv = isAutosCategory(categoryId) && isAutosDealerProfile(userType)

  const financingBannerEnabled = availableOnNpv || availableOnPv
  const tradeInBannerEnabled = !!(
    externalSourceId &&
    userType &&
    TRADE_IN_CATEGORIES.includes(categoryId) &&
    isAutosDealerProfile(userType)
  )

  if (
    (bannerType === BannerType.FINANCING && !financingBannerEnabled) ||
    (bannerType === BannerType.TRADEIN && !tradeInBannerEnabled)
  )
    return null

  const handleClick = () => {
    if (bannerType === BannerType.FINANCING) {
      openFinancingModal()
    } else if (bannerType === BannerType.TRADEIN) {
      openTradeInModal()
    }

    trackEvent(DEALER_REQUEST_CTA_ASSETS[bannerType].trackingParameters)
  }

  /* When both banners are present, they should be rendered side by side with the
  chevron design. When one banner is present it should take up the entire width of
  the VIP body, and the chevron should be replaced with a button. */
  const bothBannersEnabled = financingBannerEnabled && tradeInBannerEnabled

  const renderBannerBody = () => {
    const dataTestId =
      bannerType === BannerType.FINANCING ? 'financing-inquiry-cta' : 'trade-in-cta'

    return (
      <Flex
        gap={spacing.default}
        alignItems="center"
        justifyContent="space-between"
        data-testid={dataTestId}
      >
        <ImageCustom srcImage={DEALER_REQUEST_CTA_ASSETS[bannerType].image} alt="" skipNextImage />

        <Flex gap={spacing.mini} flexDirection="column">
          <HeadlineText size="medium" as="h2">
            {t(DEALER_REQUEST_CTA_ASSETS[bannerType].title)}
          </HeadlineText>
          <BodyText size="small" color={colors.grey.light1}>
            {t(DEALER_REQUEST_CTA_ASSETS[bannerType].description)}
          </BodyText>
        </Flex>
      </Flex>
    )
  }

  return (
    <>
      <VipDealerRequestCtaWrapper>
        {bothBannersEnabled ? (
          <VipDealerRequestCtaBanner
            as="button"
            aria-label={t(DEALER_REQUEST_CTA_ASSETS[bannerType].buttonText)}
            onClick={handleClick}
          >
            {renderBannerBody()}

            <VipDealerRequestCtaChevron>
              <ChevronIcon aria-hidden />
            </VipDealerRequestCtaChevron>
          </VipDealerRequestCtaBanner>
        ) : (
          <VipDealerRequestCtaBanner>
            {renderBannerBody()}

            <Button variant="subtle" onClick={handleClick}>
              {t(DEALER_REQUEST_CTA_ASSETS[bannerType].buttonText)}
            </Button>
          </VipDealerRequestCtaBanner>
        )}
      </VipDealerRequestCtaWrapper>

      {bannerType === BannerType.FINANCING && (
        <VipFinancingModal listingId={listingId} externalSourceId={externalSourceId} />
      )}
      {bannerType === BannerType.TRADEIN && tradeInBannerEnabled && (
        <VipTradeInModal
          listingId={listingId}
          userType={userType}
          listingUrl={listingUrl}
          externalSourceId={externalSourceId}
        />
      )}
    </>
  )
}
